import { useCallback, useState } from "react";

import { Eye, ScanText, X } from "lucide-react";

import { FilePreview, SnipResult } from "@/components/FilePreview";
import { NormalizedRect } from "@/components/FilePreview/SnipOverlay";
import { MapToReportBuilderDialog } from "@/components/MapToReportBuilderDialog";
import { ExtractType } from "@/hooks/mutations/use-module-extracts";
import { useOcrSnip } from "@/hooks/mutations/use-ocr-snip";
import { stripBundlePrefix } from "@/utils/bundle-name";
import { cn } from "@/utils/ui";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { ReactComponent as ViewEditIcon } from "@assets/svg/eye-with-pencil.svg";

import { Button } from "@components/ui/button";
import { Card } from "@components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@components/ui/dialog";

import { SummaryPanel } from "./SummaryPanel";

type FilePreviewDialogProps = {
  fileName?: string;
  fileUrl: string | null;
  fileType: string | null;
  file?: DocSorterFileSchema;
  trigger?: React.ReactNode;
  showSummaryOption?: boolean;
  onOpenChange?: (open: boolean) => void;
  caseNumber?: string;
};

export const FilePreviewDialog = ({
  fileName,
  fileUrl,
  fileType,
  file,
  trigger,
  showSummaryOption = true,
  onOpenChange,
  caseNumber,
}: FilePreviewDialogProps) => {
  const [showSummary, setShowSummary] = useState(true);
  const [snipMode, setSnipMode] = useState(false);
  const [snipResult, setSnipResult] = useState<SnipResult | null>(null);
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [textSelection, setTextSelection] = useState<{
    text: string;
    pageNumber?: number;
  } | null>(null);
  const [mapDialogText, setMapDialogText] = useState("");
  const [mapDialogExtractType, setMapDialogExtractType] =
    useState<ExtractType>("snip");

  const {
    mutate: extractSnip,
    isPending: snipIsLoading,
    error: snipError,
  } = useOcrSnip();

  const handleSnipComplete = useCallback(
    (rect: NormalizedRect) => {
      if (!file?.name || !caseNumber) return;

      setSnipResult(null);

      extractSnip(
        {
          case_number: caseNumber,
          file_name: file.name,
          page_number: rect.pageNumber,
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
        },
        {
          onSuccess: (data) => {
            if (data.status === "processing") {
              setSnipResult({
                isProcessing: true,
              });
            } else {
              setSnipResult({
                text: data.text || "",
                source: data.source || "",
                lineCount: data.line_count || 0,
                pageNumber: rect.pageNumber,
              });
            }
          },
        }
      );
    },
    [file, caseNumber, extractSnip]
  );

  const handleSnipResultClose = useCallback(() => {
    setSnipResult(null);
    setSnipMode(false);
  }, []);

  const handleTextSelected = useCallback((text: string, pageNumber?: number) => {
    setTextSelection({ text, pageNumber });
  }, []);

  const handleTextSelectionClose = useCallback(() => {
    setTextSelection(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const handleSaveNote = useCallback(() => {
    // Placeholder — will be wired up later
    const text = snipResult?.text || textSelection?.text;
    console.log("Save Note clicked", text);
  }, [snipResult, textSelection]);

  const handleMapToReportBuilder = useCallback(() => {
    setMapDialogText(snipResult?.text || textSelection?.text || "");
    setMapDialogExtractType(snipResult ? "snip" : "highlight");
    setShowMapDialog(true);
    // Clear snip/selection state so popovers close
    setSnipResult(null);
    setSnipMode(false);
    setTextSelection(null);
  }, [snipResult, textSelection]);

  return (
    <>
      <Dialog onOpenChange={onOpenChange}>
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm" disabled={!fileUrl}>
              <Eye className="size-4" />
              Full View
            </Button>
          )}
        </DialogTrigger>

        <DialogContent
          className={cn(
            "h-[95vh] shrink-0 overflow-hidden p-0 sm:max-w-5xl",
            showSummary && "sm:max-w-[85vw]"
          )}
          showCloseButton={false}
          aria-describedby={undefined}
        >
          <div
            className={cn(
              "grid h-full min-h-0",
              showSummary ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            <section className="grid h-full min-h-0 grid-rows-[auto_auto_1fr] overflow-hidden p-8">
              <div className="relative mb-2 w-full space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <DialogTitle className="mb-1 text-xl font-semibold">
                    Document Preview
                  </DialogTitle>

                  <DialogClose asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <X className="h-4 w-4" />
                    </Button>
                  </DialogClose>
                </div>

                <h3
                  className="text-muted-foreground truncate text-sm"
                  title={
                    fileName ? stripBundlePrefix(fileName) : "No document selected"
                  }
                >
                  {fileName ? stripBundlePrefix(fileName) : "No document selected"}
                </h3>

                {showSummaryOption && (
                  <div className="flex justify-end gap-2">
                    {fileType === "pdf" && caseNumber && (
                      <Button
                        variant={snipMode ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setSnipMode(!snipMode);
                          setSnipResult(null);
                          setTextSelection(null);
                        }}
                        title={snipMode ? "Exit snip mode" : "Select text region"}
                      >
                        <ScanText className="mr-2 size-4" />
                        {snipMode ? "Cancel" : "Text Capture"}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSummary(!showSummary)}
                      disabled={!file}
                    >
                      <ViewEditIcon className="mr-2 size-4" />
                      {showSummary ? "Hide AI Summary" : "View AI Summary"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="h-full min-h-0 w-full">
                <FilePreview
                  key={fileUrl || "no-file"}
                  fileUrl={fileUrl}
                  fileType={fileType as "pdf" | "image" | "other" | null}
                  filename={fileName}
                  fileLoadError={!fileUrl}
                  className="h-full"
                  snipMode={snipMode}
                  onSnipComplete={handleSnipComplete}
                  snipResult={snipResult}
                  snipIsLoading={snipIsLoading}
                  snipError={snipError?.message || null}
                  onSnipResultClose={handleSnipResultClose}
                  onSaveNote={handleSaveNote}
                  onMapToReportBuilder={handleMapToReportBuilder}
                  textSelection={textSelection}
                  onTextSelected={handleTextSelected}
                  onTextSelectionClose={handleTextSelectionClose}
                />
              </div>
            </section>

            {showSummary && (
              <section className="flex h-full min-h-0 flex-col overflow-hidden border-l border-border p-8">
                <SummaryPanel
                  file={file || null}
                  isVisible={showSummary}
                  onClose={() => setShowSummary(false)}
                  onSaveNote={(text) => {
                    console.log("Save Note clicked", text);
                  }}
                />
              </section>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <MapToReportBuilderDialog
        open={showMapDialog}
        onOpenChange={setShowMapDialog}
        text={mapDialogText}
        targetField="summary_from_doc_sorter"
        pageNumber={snipResult?.pageNumber ?? textSelection?.pageNumber}
        caseNumber={caseNumber}
        templateId={file?.case?.template?.id}
        sourceFileName={fileName}
        extractType={mapDialogExtractType}
      />
    </>
  );
};
